import { mediaDeletedEvent, mediaUpdatedEvent } from '@lib/events/eventBus';
import { isMobileViewport } from '@lib/hooks/sizing/useIsMobile';
import { FOLDER_ROOT_PATH, type FolderPath } from '@lib/library/folderPath';
import { listLibraryFiles, type LibraryFilesListing } from '@lib/network/libraryFolders';
import { isAudioMedia } from '@lib/utils/fileExtensions';
import { getDisplayProgressOrZero } from '@pages/Library/utils/mediaStatus';
import { mediaStatusPoller } from '@stores/MediaStatusPoller';
import {
  parseStoredEntries,
  restorableFailures,
  toPersistedEntries,
  TWENTY_FOUR_HOURS_MS,
  type PersistedEntry,
} from '@stores/notificationStorage';
import type { UploadFailureCategory } from '@stores/UploadManager/uploadFailure';
import { userStore } from '@stores/User';
import { Media } from '@typings/Media';
import { action, computed, makeObservable, observable, reaction } from 'mobx';

export type NotificationType =
  | 'generic-toast'
  | 'add-media-via-url'
  | 'upload-file'
  | 'notif-bell-entry'
  | 'rich-event';

interface NotificationBase {
  id: string;
  timestamp: number;
  read: boolean;
}

export interface GenericToastNotification extends NotificationBase {
  type: 'generic-toast';
  header: string;
  description?: string;
  variant: 'success' | 'error';
  /** Sonner toast ID — set for generic-toast notifications so we can dismiss the toast. */
  toastId?: string | number;
}

/** Optional structured payload attached to a notif-bell entry so its card
 * can render a "More info" affordance. Currently a single kind — a list of
 * media filenames — is enough; the union lets us add more kinds later
 * without churning the entry shape itself. */
export type NotifBellEntryDetails = {
  kind: 'media-list';
  /** Modal title shown above the list (e.g. "Deleted media"). */
  title: string;
  /** Filenames to render in the modal. */
  filenames: string[];
  /** When set, the "More info" modal renders a primary footer button that
   * invokes this callback. Used by failed bulk operations to offer a
   * "Retry all" affordance straight from the bell. Optional because
   * success-state entries (e.g. "Deleted media") have nothing to retry. */
  onRetryAll?: () => void | Promise<void>;
  /** Label for the retry button. Defaults to "Retry all" in the modal. */
  retryAllLabel?: string;
};

/** A toast that mirrored into the new general-purpose notifications bell
 * (the one mounted next to the upload-activity panel). Same payload shape
 * as `GenericToastNotification`; kept as a distinct type so the two
 * surfaces filter cleanly off `notifications`. */
export interface NotifBellEntry extends NotificationBase {
  type: 'notif-bell-entry';
  header: string;
  description?: string;
  variant: 'success' | 'error';
  /** Sonner toast ID — kept around so we can dismiss the toast from the bell. */
  toastId?: string | number;
  /** Optional richer payload (only used by the bell card, not by the
   * ephemeral visual toast). Currently drives a "More info" modal listing
   * filenames for bulk delete success/failure entries. */
  details?: NotifBellEntryDetails;
}

export interface AddMediaViaUrlNotification extends NotificationBase {
  type: 'add-media-via-url';
  /** Bold label rendered on the batch card. Defaults to the library name for
   * new uploads; overridden for retries ("Retry of all failed files", etc.).
   * Required — every caller must pass something. */
  title: string;
  // ── BatchCard thumbnail slots ───────────────────────────────────────────
  // The BatchCard renders up to two stacked card visuals: the primary tile
  // (always shown) and a faked stack-backdrop peeking out behind it (only
  // when mediaCount > 1, hinting that the batch holds multiple files).
  //
  // Each slot has a (thumbnail, gradientSeed) pair:
  //   - thumbnail*Url  — preferred. The actual file's preview image (data
  //                      URL captured client-side, or backend URL for URL
  //                      imports). Drawn as the slot's <img> when set.
  //   - *gradientSeed  — fallback. When the thumbnail is null (extraction
  //                      failed, video too large, etc.), the slot renders
  //                      a `PlaceholderGradient` colour-seeded from this.
  //                      Set to the first/second file's content fingerprint
  //                      (or backend uniqueId for URL imports) so the same
  //                      media gets the same placeholder colour everywhere
  //                      it appears — library grid cards seed off the same
  //                      value (see VirtualUploadCard / MediaCard). Null
  //                      falls back to a hash of notification.id.
  /** Primary thumbnail URL (or data URL), or null for placeholder. */
  thumbnailUrl: string | null;
  /** Stack-backdrop thumbnail URL (or data URL), or null for placeholder. */
  secondaryThumbnailUrl: string | null;
  /** Primary gradient seed — first file's fingerprint / uniqueId. */
  gradientSeed: string | null;
  /** Stack-backdrop gradient seed — second file's fingerprint / uniqueId. */
  secondaryGradientSeed: string | null;
  /** Whether the media is audio (controls placeholder icon). */
  isAudio: boolean;
  /** Processing progress 0–1 for the batch. */
  progress: number;
  /** Backend media IDs in this batch. */
  mediaIds: string[];
  /** Media IDs that failed during backend processing (media.status === 'errored').
   * Populated by polling; may be empty while processing is still in-flight. */
  erroredMediaIds: string[];
  /** Library ID (needed to poll media status). */
  libraryId: string;
  /** The library folder the batch was filed in — where a retry files it again. A file dropped
   * inside a directory keeps its own subfolder under this on the `File` itself. */
  folderPath: FolderPath;
  /** Library name for display. */
  libraryName: string;
  /** Media titles for tooltip display. */
  mediaTitles: string[];
  /** Timestamp when processing completed (progress reached 1), or null if still active. */
  completedAt: number | null;
}

/** Per-file row inside a rich-event card. Shaped so it converts trivially to
 * a `FailedRow` for the FailedMediaListModal when the card's "View details"
 * action opens the modal. Audio-vs-video icon fallback follows the same
 * convention as the modal (defaults to video). */
export type RichEventRow = {
  /** Unique-within-the-event key. Filename is usually enough; for processing
   * errors we use the mediaId so two media with the same name don't collide. */
  key: string;
  filename: string;
  reason?: string;
  isAudio?: boolean;
  thumbnailUrl?: string | null;
  /** When set, the modal row renders a "View in library" button that
   * navigates to the existing media. Used by duplicate rich events
   * (drag-drop-all-duplicates). */
  viewInLibrary?: {
    libraryId: string;
    mediaId: string;
    libraryName?: string;
    mediaName?: string;
  };
};

/** Visual variant for a rich-event card: drives the thumbnail-fallback icon
 * and the card's accent colour. Keep this list in sync with `RichEventCard`. */
export type RichEventIconKind = 'file-rejected' | 'duplicate' | 'processing-error';

/** "Big card" event entry shown in the upload activity panel. Distinct from
 * generic-toast (which is a small row) and from batch cards (which represent
 * actual upload batches). Used by drag-drop/duplicate/billing/processing-error
 * events that have a list of affected files and a primary CTA. */
export interface RichEventNotification extends NotificationBase {
  type: 'rich-event';
  variant: 'error' | 'warning' | 'success';
  iconKind: RichEventIconKind;
  /** Header text. May contain the placeholder `{filename}` — the card
   * substitutes it with `descriptionFilename` (or `rows[0].filename` if
   * unset) inside a single-line truncating span so long names get an
   * ellipsis without breaking the rest of the sentence. */
  header: string;
  /** Description text. Supports the same `{filename}` placeholder as
   * `header`. */
  description?: string;
  /** Concrete filename to substitute into `{filename}` placeholders in
   * `header` / `description`. When omitted, the card falls back to
   * `rows[0]?.filename`. */
  descriptionFilename?: string;
  /** When set, the card finds this substring inside `description` and
   * renders it as a click-to-open-modal span (dotted underline). Used by
   * the multi-file rejection card so "N files" inside the description is
   * itself a modal trigger, on top of the rectangle below. */
  descriptionLinkText?: string;
  rows: RichEventRow[];
  /** Optional secondary line under the title — matches the BatchCard's
   * `Library Name | <status>` strip. When omitted the card falls through
   * to header + description only. Used by the duplicate card to read as
   * a sibling of the BatchCard rather than a separate visual family. */
  subtitle?: {
    libraryName: string;
    libraryUrl?: string;
    statusText: string;
    /** Tints the status icon + text. Defaults to warning (yellow). */
    statusVariant?: 'error' | 'warning' | 'success' | 'info';
  };
  /** Title used by the FailedMediaListModal when "View details" is clicked. */
  modalTitle: string;
  /** Optional override for the "View N files" rectangle label. Defaults to
   * `View` + pluralised `file` count. Use to surface intent — e.g. a
   * rejection card sets this to `"View N rejected files"`. */
  viewLinkAction?: string;
  /** Optional primary CTA — e.g. "Add credits" / "Upgrade plan". When
   * omitted, the card's only action is the click-anywhere "View details"
   * affordance that opens the modal. */
  primaryAction?: { label: string; onClick: () => void };
  /** Library this event belongs to — when set, drives the rolling-window
   * merge for `batchKind: 'media-processing-errored'`. Unused for events
   * with no natural library scope (e.g. drag-drop rejections at root). */
  libraryId?: string;
  /** Marks the entry as a merge target for the rolling-window helper. Only
   * `'media-processing-errored'` participates today; others always create
   * a fresh entry. */
  batchKind?: 'media-processing-errored';
}

/** Per-file row for the "View N duplicates skipped" affordance on a
 * BatchCard. When `libraryId` / `mediaId` are set, the modal row renders
 * a "View in library" button that navigates to the existing media. */
export type SkippedDuplicateRow = {
  key: string;
  filename: string;
  /** Library + media ID of the existing media this file matched against. */
  libraryId?: string;
  mediaId?: string;
};

/** A file in an upload batch whose upload failed, so no media came of it. Unlike the
 * File-bearing entry in `UploadManager.rejectedFiles`, this survives a reload and
 * stays when the user retries or dismisses that entry: it records what happened
 * to the batch, not what is still left to act on. */
export type FailedUpload = {
  fileName: string;
  reason: string;
  category: UploadFailureCategory;
};

export interface UploadFileNotification extends NotificationBase {
  type: 'upload-file';
  /** Bold label rendered on the batch card. Defaults to the library name for
   * new uploads; overridden for retries ("Retry of all failed files", etc.).
   * Required — every caller must pass something. */
  title: string;
  // ── BatchCard thumbnail slots ───────────────────────────────────────────
  // Same primary/stack-backdrop pair model as `AddMediaViaUrlNotification`
  // — see the comment block above for the full explanation. For file
  // uploads the thumbnail is a client-side data URL captured at queue time;
  // gradientSeed is the file's content fingerprint so the placeholder
  // colour matches the library grid card for the same media.
  thumbnailUrl: string | null;
  secondaryThumbnailUrl: string | null;
  gradientSeed: string | null;
  secondaryGradientSeed: string | null;
  /** Whether the media is audio (controls placeholder icon). */
  isAudio: boolean;
  /** Upload transfer progress 0–1. */
  uploadProgress: number;
  /** The slice of uploadProgress (0–1) contributed by FAILED/cancelled files.
   *  Terminal-but-failed files count toward uploadProgress so the bar completes,
   *  but this lets the UI render that slice distinctly (red) instead of as
   *  success. */
  failedUploadProgress?: number;
  /** Backend processing progress 0–1. */
  processingProgress: number;
  /** Local upload UUIDs in this batch (used to aggregate upload progress). */
  uploadUuids: string[];
  /** Backend media IDs (populated after uploads complete, needed for processing poll). */
  mediaIds: string[];
  /** Media IDs that failed during backend processing (media.status === 'errored').
   * Populated by polling; may be empty while processing is still in-flight. */
  erroredMediaIds: string[];
  /** Files that failed in the upload phase. Append-only — see FailedUpload. */
  failedUploads: FailedUpload[];
  /** Library ID. */
  libraryId: string;
  /** The library folder the batch was filed in — where a retry files it again. A file dropped
   * inside a directory keeps its own subfolder under this on the `File` itself. */
  folderPath: FolderPath;
  /** Library name for display. */
  libraryName: string;
  /** Media titles for tooltip display. */
  mediaTitles: string[];
  /** Timestamp when processing completed, or null if still active. */
  completedAt: number | null;
  /** Files filtered out as duplicates *before* the batch was created — the
   * non-duplicate files in the batch still uploaded. Surfaced on the
   * BatchCard via a "View N duplicates skipped" `MediaListLink` (info
   * variant). Empty / undefined hides the affordance. */
  skippedDuplicates?: SkippedDuplicateRow[];
}

export type Notification =
  | GenericToastNotification
  | AddMediaViaUrlNotification
  | UploadFileNotification
  | NotifBellEntry
  | RichEventNotification;

/** Notifications that represent an upload batch (file uploads or URL imports).
 * Anything with mediaIds, error tracking, progress, and a source folder — i.e.
 * everything except `generic-toast`. */
export type BatchNotification = UploadFileNotification | AddMediaViaUrlNotification;

export function isBatchNotification(n: Notification): n is BatchNotification {
  return n.type === 'upload-file' || n.type === 'add-media-via-url';
}

type NotificationInput =
  | Omit<GenericToastNotification, 'id' | 'timestamp' | 'read'>
  | Omit<AddMediaViaUrlNotification, 'id' | 'timestamp' | 'read' | 'erroredMediaIds'>
  | Omit<UploadFileNotification, 'id' | 'timestamp' | 'read' | 'erroredMediaIds'>
  | Omit<NotifBellEntry, 'id' | 'timestamp' | 'read'>
  | Omit<RichEventNotification, 'id' | 'timestamp' | 'read'>;

/** Pre-scoping global key from older builds. Cleared on first setCurrentUser
 * because we can't safely attribute its contents to any specific user (it may
 * have been written by a different account on a shared device). */
const LEGACY_STORAGE_KEY = 'sz_media_notifications';
const STORAGE_KEY_PREFIX = 'sz_media_notifications_';
const getStorageKey = (userId: string) => `${STORAGE_KEY_PREFIX}${userId}`;

/** Window during which two `media-processing-errored` events for the same
 * library collapse into one rich-event card instead of creating a new one. */
const ROLLING_MERGE_WINDOW_MS = 60 * 1000;

let nextId = 0;

export class NotificationStore {
  @observable
  notifications: Notification[] = [];

  /** Whether the upload-activity panel popover is open. Drives panel-side
   * read state + polling. The new notif-bell has its own observable below
   * (`notifBellPopoverOpen`) — they're independent surfaces. */
  @observable
  popoverOpen = false;

  /** Whether the new notif-bell popover is open. Drives bell-side read
   * state. Independent from `popoverOpen` (the upload-activity panel) so
   * opening one surface doesn't silently clear the other's unread badge. */
  @observable
  notifBellPopoverOpen = false;

  /** Unread count on the upload-activity panel — counts everything except
   * notif-bell entries (those land in the separate bell). */
  @computed
  get unreadCount() {
    return this.notifications.filter((n) => !n.read && n.type !== 'notif-bell-entry').length;
  }

  /** Unread count on the new notif-bell — counts unread notif-bell entries
   * only. Used by the bell icon's badge. */
  @computed
  get notifBellUnreadCount() {
    return this.notifications.filter((n) => !n.read && n.type === 'notif-bell-entry').length;
  }

  /** Disposers for the libraries currently registered with `mediaStatusPoller`,
   * keyed by libraryId. This store no longer owns any timer — the
   * poller does — so "polling" here means holding a registration. */
  private pollerRegistrations = new Map<string, () => void>();

  /** Disposers for the event-bus subscriptions that feed batch state. */
  private eventDisposers: (() => void)[] = [];

  private disposeActiveNotifReaction: (() => void) | null = null;

  /** Stashed on setCurrentUser so calls made before restoreFromStorage runs
   * don't overwrite real data. */
  private stashedEntries: PersistedEntry[] = [];

  /** The user whose notifications are currently in memory and being persisted.
   * Set via setCurrentUser() once the authenticated user ID is known. While
   * null, persistToStorage() is a no-op so we never write notifications to a
   * key we can't scope to a user. */
  private currentUserId: string | null = null;

  /** While true, `persistToStorage()` is a no-op. Set only by
   * `restoreFromStorage` so the per-`add()` persists inside the restore loop
   * don't narrow disk storage to a partial snapshot — if the restore bails
   * mid-loop (user switch), the on-disk entries are left untouched. The
   * single `persistToStorage()` at the end of restore flushes the final
   * state. */
  private _suppressPersistDuringRestore = false;

  private persistScheduled = false;

  constructor() {
    makeObservable(this);
    // Storage is read in setCurrentUser() once we know which user's
    // notifications to load. Without that we can't scope the key correctly.

    // Batch state is driven by the shared poller's fan-out rather than by a
    // timer of our own. These two subscriptions are the whole input path.
    this.eventDisposers.push(
      mediaUpdatedEvent.on(({ libraryId, media }) => this.applyMediaUpdate(libraryId, media)),
      mediaDeletedEvent.on(({ libraryId, mediaIds }) =>
        this.applyMediaDeleted(libraryId, mediaIds),
      ),
    );

    // Register the libraries of active batches with the poller while the panel is
    // open. Keyed on the library set (not just `popoverOpen`) so a batch that
    // starts or finishes while the panel is open re-registers correctly.
    this.disposeActiveNotifReaction = reaction(
      () => (this.popoverOpen ? this.activeBatchLibraryKeys : ''),
      () => this.syncPollerRegistrations(),
      { fireImmediately: true },
    );
  }

  /** The libraries active batches live in. */
  @computed
  private get activeBatchLibraryIds(): Set<string> {
    return new Set(this.activeMediaNotifications.map((n) => n.libraryId));
  }

  /** Stable string identity of the above, so the reaction above fires on a
   * genuine change rather than on every poll cycle mutating notification
   * progress. */
  @computed
  private get activeBatchLibraryKeys(): string {
    return [...this.activeBatchLibraryIds].sort().join(',');
  }

  /**
   * Hold exactly one poller registration per library an active batch lives in.
   *
   * The panel gate is preserved from the old `popoverOpen`-driven timer: the
   * batch card is only visible in the panel, so there is nothing to update
   * while it is shut. The library page holds its own registration, so status
   * still refreshes there with the panel closed.
   */
  private syncPollerRegistrations() {
    const wanted = this.popoverOpen ? this.activeBatchLibraryIds : new Set<string>();

    for (const [libraryId, dispose] of [...this.pollerRegistrations]) {
      if (!wanted.has(libraryId)) {
        dispose();
        this.pollerRegistrations.delete(libraryId);
      }
    }

    let added = false;
    for (const libraryId of wanted) {
      if (this.pollerRegistrations.has(libraryId)) continue;
      this.pollerRegistrations.set(libraryId, mediaStatusPoller.register(libraryId));
      added = true;
    }

    // Opening the panel onto an in-flight batch should not show stale progress
    // for a whole interval.
    if (added) mediaStatusPoller.refreshNow();
  }

  /** Bind the store to a user. Reads that user's persisted entries into the
   * stash (consumed by the next restoreFromStorage call) and enables writes
   * scoped to their storage key. Safe to call repeatedly; only does work when
   * the userId actually changes. When the user changes in-place (logout +
   * login without a page refresh), wipes the previous user's in-memory
   * notifications so they don't bleed into the new session. */
  @action
  setCurrentUser(userId: string) {
    if (this.currentUserId === userId) return;
    if (this.currentUserId !== null) {
      this.notifications = [];
    }
    this.currentUserId = userId;
    this.stashedEntries = this.readStorage();
    try {
      localStorage.removeItem(LEGACY_STORAGE_KEY);
    } catch {
      // localStorage may be unavailable
    }
  }

  @computed
  get sorted() {
    return [...this.notifications].sort((a, b) => b.timestamp - a.timestamp);
  }

  @computed
  get count() {
    return this.notifications.length;
  }

  @computed
  get activeMediaNotifications() {
    return this.notifications.filter(
      (n): n is AddMediaViaUrlNotification | UploadFileNotification => {
        if (n.type === 'add-media-via-url') return n.progress < 1;
        if (n.type === 'upload-file')
          return n.uploadProgress >= 1 && n.processingProgress < 1 && n.mediaIds.length > 0;
        return false;
      },
    );
  }

  @action
  add(notification: NotificationInput): string {
    const id = `notif-${nextId++}`;
    // Bell entries are read iff the bell popover is open; everything else
    // (upload-panel entries) reads off the upload-panel popover.
    const initialRead =
      notification.type === 'notif-bell-entry' ? this.notifBellPopoverOpen : this.popoverOpen;
    const base = {
      ...notification,
      id,
      timestamp: Date.now(),
      read: initialRead,
    };
    // Auto-initialize erroredMediaIds for batch notifications — populated by polling.
    const withDefaults =
      notification.type === 'add-media-via-url' || notification.type === 'upload-file'
        ? { ...base, erroredMediaIds: [] as string[] }
        : base;
    this.notifications.push(withDefaults as Notification);
    this.persistToStorage();
    return id;
  }

  @action
  remove(id: string) {
    this.notifications = this.notifications.filter((n) => n.id !== id);
    this.persistToStorage();
  }

  @action
  updateProgress(id: string, progress: number) {
    const n = this.notifications.find((n) => n.id === id);
    // Bail when nothing moved. This is called on every poll cycle and each
    // call ends in `persistToStorage()` — a synchronous JSON.stringify of every
    // batch's ids and titles plus a localStorage write.
    if (n && n.type === 'add-media-via-url' && n.progress === progress) return;
    if (n && n.type === 'upload-file' && n.processingProgress === progress) return;
    if (n && n.type === 'add-media-via-url') {
      n.progress = progress;
      if (progress >= 1) {
        n.completedAt = Date.now();
      }
      this.persistToStorage();
    } else if (n && n.type === 'upload-file') {
      n.processingProgress = progress;
      if (progress >= 1) {
        n.completedAt = Date.now();
      }
      this.persistToStorage();
    }
  }

  @action
  updateUploadProgress(id: string, uploadProgress: number, failedProgress: number = 0) {
    // IMPORTANT: mutate the property directly — do NOT replace the array slot
    // (e.g. `this.notifications[idx] = { ...n, uploadProgress }`). An array-slot
    // replacement does not consistently trigger re-renders of observer components
    // that read the property off the existing notification reference, so the
    // upload progress bar in the panel silently stops updating.
    const n = this.notifications.find((n) => n.id === id);
    if (n && n.type === 'upload-file') {
      n.uploadProgress = uploadProgress;
      n.failedUploadProgress = failedProgress;
    }
  }

  /**
   * Mark a batch as cancelled. Pushes the upload-file notification straight
   * to terminal completion (uploadProgress + processingProgress = 1) when no
   * media survived (all files cancelled), so the BatchCard state machine
   * lands on 'cancelled-all' instead of getting stuck at 'processing' waiting
   * for a processingProgress signal that no longer applies. Mixed-survival
   * batches keep their normal processingProgress flow — the aggregator + poll
   * cycle handle the survivors as usual; only mediaIds/Titles are pruned.
   */
  @action
  markBatchCancelled(id: string, survivedMediaIds: string[], survivedMediaTitles: string[]) {
    const n = this.notifications.find((n) => n.id === id);
    if (!n || n.type !== 'upload-file') return;
    n.mediaIds = survivedMediaIds;
    n.mediaTitles = survivedMediaTitles;
    if (survivedMediaIds.length === 0) {
      n.uploadProgress = 1;
      n.processingProgress = 1;
    }
    this.persistToStorage();
  }

  /** Attach the list of files that were filtered out as duplicates to an
   * upload-file batch notification. Drives the BatchCard's "View N
   * duplicates skipped" `MediaListLink`. No-op when the notification is
   * gone or isn't an upload-file batch. */
  @action
  setSkippedDuplicates(id: string, rows: SkippedDuplicateRow[]) {
    const n = this.notifications.find((n) => n.id === id);
    if (n && n.type === 'upload-file') {
      n.skippedDuplicates = rows;
    }
  }

  @action
  transitionToProcessing(id: string, mediaIds: string[], mediaTitles: string[]) {
    const n = this.notifications.find((n) => n.id === id);
    if (n && n.type === 'upload-file') {
      n.uploadProgress = 1;
      n.mediaIds = mediaIds;
      n.mediaTitles = mediaTitles;
      this.persistToStorage();
    }
  }

  @action
  recordFailedUpload(id: string, failure: FailedUpload) {
    const n = this.notifications.find((n) => n.id === id);
    if (n?.type !== 'upload-file') return;
    n.failedUploads.push(failure);
    this.schedulePersist();
  }

  @action
  setPopoverOpen(open: boolean) {
    this.popoverOpen = open;
    if (open) {
      // Only mark upload-panel entries as read — bell entries are their own
      // surface and shouldn't get auto-cleared by opening the panel.
      for (const n of this.notifications) {
        if (n.type !== 'notif-bell-entry') n.read = true;
      }
      this.persistToStorage();
    }
  }

  @action
  setNotifBellPopoverOpen(open: boolean) {
    this.notifBellPopoverOpen = open;
    if (open) {
      // Symmetric counterpart: opening the bell marks bell entries read,
      // leaves the upload-panel's read state alone.
      for (const n of this.notifications) {
        if (n.type === 'notif-bell-entry') n.read = true;
      }
      this.persistToStorage();
    }
  }

  /** Drops every notif-bell entry — bell-side counterpart to `clearCompleted`
   * (which is upload-panel-only). Leaves upload-panel entries untouched. */
  @action
  clearNotifBell() {
    this.notifications = this.notifications.filter((n) => n.type !== 'notif-bell-entry');
    this.persistToStorage();
  }

  @action
  clear() {
    this.notifications = [];
    this.persistToStorage();
  }

  /** Push a fresh rich-event entry and open the upload-activity panel so
   * the user sees it immediately — matches the UX convention used by
   * `UploadManager.uploadFiles`. Convenience over a raw `add` call. For
   * the `media-processing-errored` rolling-window merge, use
   * `pushRichEventOrMerge` instead. */
  @action
  pushRichEvent(entry: Omit<RichEventNotification, 'id' | 'timestamp' | 'read'>): string {
    const id = this.add(entry);
    // Desktop only — see setPopoverOpen note below. On mobile the entry stays
    // unread (added while popoverOpen is false) so the bell FAB badges it.
    if (!isMobileViewport()) this.setPopoverOpen(true);
    return id;
  }

  /** For `media-processing-errored` rich events: if an entry already exists
   * for the same library within the last 60 seconds, append the new rows
   * (deduped by row.key) instead of creating a new card — keeps the panel
   * from getting spammed when many media error in quick succession. For
   * events with no `libraryId` / `batchKind`, this just delegates to `add`.
   *
   * Rolling window: each successful merge bumps the entry's `timestamp` so
   * the 60-second clock restarts from the most recent failure rather than
   * from the original one. Stops cards from auto-locking after the first
   * failure when subsequent ones arrive every few seconds. */
  @action
  pushRichEventOrMerge(entry: Omit<RichEventNotification, 'id' | 'timestamp' | 'read'>): string {
    if (entry.batchKind === 'media-processing-errored' && entry.libraryId) {
      const now = Date.now();
      const existing = this.notifications.find(
        (n): n is RichEventNotification =>
          n.type === 'rich-event' &&
          n.batchKind === 'media-processing-errored' &&
          n.libraryId === entry.libraryId &&
          now - n.timestamp < ROLLING_MERGE_WINDOW_MS,
      );
      if (existing) {
        const seen = new Set(existing.rows.map((r) => r.key));
        const newRows = entry.rows.filter((r) => !seen.has(r.key));
        if (newRows.length > 0) {
          existing.rows = [...existing.rows, ...newRows];
          existing.timestamp = now;
          // Refresh the header so the count stays accurate as the card grows.
          existing.header = entry.header;
          existing.description = entry.description;
          if (!this.popoverOpen) existing.read = false;
        }
        if (!isMobileViewport() && this.popoverOpen === false) this.setPopoverOpen(true);
        return existing.id;
      }
    }
    return this.pushRichEvent(entry);
  }

  /** Whether a notification is still "in progress" — has work the user is
   * waiting on and would lose visibility into if it were cleared. Source of
   * truth for the bell's `clearCompleted()` action and any UI that needs to
   * decide whether a notification is safe to drop. */
  isActive(n: Notification): boolean {
    if (n.type === 'add-media-via-url') return n.progress < 1;
    if (n.type === 'upload-file') return n.uploadProgress < 1 || n.processingProgress < 1;
    return false;
  }

  /** Drops every completed notification, keeping in-progress ones so the user
   * doesn't accidentally lose visibility into work that's still uploading or
   * processing. Backs the bell's "Clear all" button — the full-wipe `clear()`
   * is reserved for debug-page resets. */
  @action
  clearCompleted() {
    this.notifications = this.notifications.filter((n) => this.isActive(n));
    this.persistToStorage();
  }

  /** Restore add-media-via-url notifications from localStorage. Call after user is authenticated. */
  async restoreFromStorage() {
    const entries = this.stashedEntries;
    this.stashedEntries = [];
    if (entries.length === 0) return;

    // Snapshot the user this restore was kicked off for. If the user
    // switches in-place (logout + login as a different account) while we
    // await API calls below, bail — otherwise add()/persistToStorage()
    // would write the previous user's restored entries under the new
    // user's storage key, defeating the per-user scoping.
    const owningUserId = this.currentUserId;
    const now = Date.now();

    // Suppress per-add persists during the restore loop so a mid-loop bail
    // (user switch or thrown error) can't narrow disk to a partial snapshot.
    // The final persistToStorage() below flushes the full restored state in
    // one write; on bail, finally resets the flag and the early `return`
    // skips the final persist, leaving disk untouched.
    this._suppressPersistDuringRestore = true;
    // Per-restore caches. Retained batches overwhelmingly share one library,
    // and the listing is unprojected — without these, ten persisted batches
    // meant ten identical whole-library payloads fetched serially on the login
    // critical path.
    const listingCache = new Map<string, LibraryFilesListing | null>();
    const libraryNameCache = new Map<string, string | null>();
    try {
      for (const entry of entries) {
        if (this.currentUserId !== owningUserId) return;

        // Skip completed entries older than 24h
        if (entry.completedAt && now - entry.completedAt >= TWENTY_FOUR_HOURS_MS) continue;

        // Fetch library name and all media in batch in parallel
        let isAudio = false;
        let initialProgress = 0;
        let libraryName = 'library';
        let mediaTitles: string[] = [];
        let completedAt = entry.completedAt;
        // Errored IDs are computed inside the try (where we have fetched media
        // status) and applied to the notification after add() completes.
        let restoredErroredMediaIds: string[] = [];
        const failedUploads = restorableFailures(entry);
        if (!failedUploads) continue;

        try {
          // One library listing instead of one GET per persisted mediaId. This
          // runs at login across up to 30 days of entries, so the old fan-out
          // was an O(N) burst at the worst possible moment.
          const [libraryName_, listing] = await Promise.all([
            libraryNameCache.has(entry.libraryId)
              ? Promise.resolve(libraryNameCache.get(entry.libraryId) ?? null)
              : userStore
                  .apiWithAuth<{ name?: string }>(`/library/${entry.libraryId}`)
                  .then((r) => r?.name ?? null)
                  .catch(() => null)
                  .then((name) => {
                    libraryNameCache.set(entry.libraryId, name);
                    return name;
                  }),
            entry.mediaIds.length === 0
              ? Promise.resolve(null)
              : listingCache.has(entry.libraryId)
                ? Promise.resolve(listingCache.get(entry.libraryId) ?? null)
                : listLibraryFiles(entry.libraryId)
                    .catch(() => null)
                    .then((data) => {
                      listingCache.set(entry.libraryId, data);
                      return data;
                    }),
          ]);
          const libraryResult = libraryName_ ? { name: libraryName_ } : null;

          if (this.currentUserId !== owningUserId) return;

          if (libraryResult?.name) {
            libraryName = libraryResult.name;
          }

          if (entry.mediaIds.length === 0) {
            // Nothing in the batch reached the server, so there is no media to
            // look up — only its failures are left to describe.
            mediaTitles = failedUploads.map((f) => f.fileName);
            initialProgress = 1;
            completedAt ??= now;
          } else if (listing === null) {
            // A failed listing tells us nothing about the media. Restore the
            // entry as persisted and let the poller reconcile once the panel
            // opens — the old code read any failure as "deleted" and silently
            // dropped the user's batch.
            mediaTitles = entry.mediaIds.map(() => 'Untitled');
            initialProgress = entry.completedAt ? 1 : 0;
          } else {
            const byId = new Map((listing.files ?? []).map((m) => [m._id, m]));
            const survivingMediaIds: string[] = [];
            const valid: Media[] = [];
            for (const mediaId of entry.mediaIds) {
              const media = byId.get(mediaId);
              if (media) {
                survivingMediaIds.push(mediaId);
                valid.push(media);
              }
            }

            if (valid.length === 0) {
              // None of the batch is in the library. Safe to drop: unlike the
              // per-media path this rests on a listing that *succeeded*, not on
              // N failed requests.
              continue;
            } else {
              // Update entry with only surviving media
              entry.mediaIds = survivingMediaIds;

              isAudio = isAudioMedia(valid[0]);
              mediaTitles = valid.map(
                (m) => m.metadata?.name ?? m.metadata?.filename ?? 'Untitled',
              );

              // Capture which of the surviving media are in error state.
              restoredErroredMediaIds = valid
                .filter((m) => m.status === 'errored')
                .map((m) => m._id);

              const allDone = valid.every(
                (m) => m.status === 'completed' || m.status === 'errored',
              );

              if (allDone) {
                initialProgress = 1;
                if (!completedAt) completedAt = now;
              } else {
                // Compute real progress from current media state
                const totalDisplayProgress = valid.reduce((sum, m) => {
                  if (m.status === 'completed' || m.status === 'errored') return sum + 100;
                  return sum + getDisplayProgressOrZero(m);
                }, 0);
                const realProgress = totalDisplayProgress / (entry.mediaIds.length * 100);
                initialProgress = realProgress;
              }
            }
          }
        } catch {
          // Network error — still create the notification so polling can clean it up
        }

        // Use add() but then override timestamp and completedAt from persisted data
        const entryType = entry.type ?? 'add-media-via-url';
        let id: string;

        // Legacy entries persisted before the title field existed fall back to
        // the library name, which was the old single source of the card label.
        const restoredTitle = entry.title ?? libraryName;

        const restoredFolderPath = entry.folderPath ?? FOLDER_ROOT_PATH;

        if (entryType === 'upload-file') {
          id = this.add({
            type: 'upload-file',
            title: restoredTitle,
            thumbnailUrl: entry.thumbnailUrl,
            secondaryThumbnailUrl: entry.secondaryThumbnailUrl ?? null,
            gradientSeed: entry.gradientSeed ?? null,
            secondaryGradientSeed: entry.secondaryGradientSeed ?? null,
            isAudio,
            uploadProgress: 1, // upload was already complete when persisted
            processingProgress: initialProgress,
            // not recoverable after refresh — uploads can't resume across a
            // page reload, so the local upload manager has no entries for this
            // batch on restore. Side-effect: the BatchCard never enters the
            // `isUploading` state for restored batches and Cancel batch is
            // implicitly disabled. When/if upload-resume is implemented this
            // should be restored from storage too so the cancel affordance
            // works for in-flight uploads after a refresh.
            uploadUuids: [],
            mediaIds: entry.mediaIds,
            failedUploads,
            libraryId: entry.libraryId,
            folderPath: restoredFolderPath,
            libraryName,
            mediaTitles,
            completedAt,
          });
        } else {
          id = this.add({
            type: 'add-media-via-url',
            title: restoredTitle,
            thumbnailUrl: entry.thumbnailUrl,
            secondaryThumbnailUrl: entry.secondaryThumbnailUrl ?? null,
            gradientSeed: entry.gradientSeed ?? null,
            secondaryGradientSeed: entry.secondaryGradientSeed ?? null,
            isAudio,
            progress: initialProgress,
            mediaIds: entry.mediaIds,
            libraryId: entry.libraryId,
            folderPath: restoredFolderPath,
            libraryName,
            mediaTitles,
            completedAt,
          });
        }

        // Restore original timestamp, read state, and skip leading fake
        const addedNotif = this.notifications.find((n) => n.id === id);
        if (addedNotif) {
          addedNotif.timestamp = entry.timestamp;
          addedNotif.read = entry.read ?? false;
          if (addedNotif.type === 'add-media-via-url' || addedNotif.type === 'upload-file') {
            // Re-derive errored media IDs from the fetched status so the Results
            // modal and batch card reflect processing failures after refresh.
            addedNotif.erroredMediaIds = restoredErroredMediaIds;
          }
          if (addedNotif.type === 'add-media-via-url') {
            addedNotif.progress = initialProgress;
          } else if (addedNotif.type === 'upload-file') {
            addedNotif.processingProgress = initialProgress;
          }
        }
      }
    } finally {
      this._suppressPersistDuringRestore = false;
    }

    if (this.currentUserId !== owningUserId) return;

    // Re-persist to clean up any expired entries and flush the suppressed
    // per-add persists from the restore loop.
    this.persistToStorage();
  }

  /** One `persistToStorage()` for a burst of writes in the same task. Failures
   * arrive in bursts — a 402 rejects every remaining file of the batch back to
   * back — and each persist re-serializes every batch, so writing per failure
   * is quadratic in the batch size. */
  private schedulePersist() {
    if (this.persistScheduled) return;
    this.persistScheduled = true;
    setTimeout(() => {
      this.persistScheduled = false;
      this.persistToStorage();
    }, 0);
  }

  private persistToStorage() {
    // Suppressed during restoreFromStorage so per-add persists don't narrow
    // disk to a partial snapshot if the restore bails mid-loop. The final
    // persist at the end of restoreFromStorage flushes the full state.
    if (this._suppressPersistDuringRestore) return;
    // Without a known user we can't pick a storage key. Skipping is safe —
    // the next setCurrentUser + restoreFromStorage cycle will rehydrate from
    // whatever was last written for that user.
    if (!this.currentUserId) return;
    const key = getStorageKey(this.currentUserId);
    const entries = toPersistedEntries(this.notifications, Date.now());

    try {
      if (entries.length === 0) {
        localStorage.removeItem(key);
      } else {
        localStorage.setItem(key, JSON.stringify(entries));
      }
    } catch {
      // localStorage may be full or unavailable
    }
  }

  private readStorage(): PersistedEntry[] {
    if (!this.currentUserId) return [];
    try {
      const raw = localStorage.getItem(getStorageKey(this.currentUserId));
      if (!raw) return [];
      return parseStoredEntries(JSON.parse(raw), Date.now());
    } catch {
      return [];
    }
  }

  /**
   * Recompute batch state after `mediaStatusPoller` has refreshed a library.
   *
   * Replaces the old per-media fan-out (one GET per mediaId every 3s, which is
   * what blew the rate limit). The emission only tells us *that* something in
   * this library moved; the batch-wide facts are read from the poller's
   * snapshot, since the payload is a delta.
   */
  @action
  private applyMediaUpdate(libraryId: string, media: Media[]) {
    if (media.length === 0) return;
    // The subscription is unconditional, so this also runs with the panel shut
    // and no batches in flight.
    if (this.activeMediaNotifications.length === 0) return;

    for (const notif of this.activeMediaNotifications) {
      if (notif.libraryId !== libraryId) continue;

      // Read the whole batch's current state from the poller's snapshot, NOT
      // from `media`. The emission is a delta — an id missing from it means
      // "unchanged", not "gone" — so computing batch-wide facts off the payload
      // made progress collapse, wiped errored ids, and left batches that could
      // never reach 100%.
      const { found: known, absent } = mediaStatusPoller.getSnapshot(notif.mediaIds);

      // Track which media have errored so BatchCard + Results modal can show
      // processing-phase failures without re-fetching. Computed over the whole
      // batch, so a media that errored on an earlier tick is not forgotten
      // just because it did not change on this one.
      const erroredIds = known.filter((m) => m.status === 'errored').map((m) => m._id);
      const erroredChanged =
        erroredIds.length !== notif.erroredMediaIds.length ||
        erroredIds.some((id, i) => id !== notif.erroredMediaIds[i]);
      if (erroredChanged) {
        notif.erroredMediaIds = erroredIds;
      }

      // Never listed at all. NOT treated as deleted — absence is not evidence.
      // Confirm out of band; only a real 404 comes back as a deletion, via
      // mediaDeletedEvent.
      if (absent.length > 0) {
        void mediaStatusPoller.confirmMissing(libraryId, absent);
      }

      // `allDone` and the progress denominator both span the whole batch, so
      // media still awaiting confirmation must not count as finished — hence
      // `absent.length === 0` rather than `known.every(...)` alone. Getting
      // this wrong would close the batch card early on a transient miss.
      const allKnownDone = known.every((m) => m.status === 'completed' || m.status === 'errored');
      if (allKnownDone && absent.length === 0) {
        this.updateProgress(notif.id, 1);
        continue;
      }

      // Average the display progress (0–100) across all media in the batch and
      // normalise to 0–1. Media with no known progress yet (queued, or processing
      // before the poller has merged its progress in) contributes 0.
      const totalDisplayProgress = known.reduce((sum, m) => {
        if (m.status === 'completed' || m.status === 'errored') return sum + 100;
        return sum + getDisplayProgressOrZero(m);
      }, 0);
      const realProgress = totalDisplayProgress / (notif.mediaIds.length * 100);

      this.updateProgress(notif.id, Math.min(realProgress, 1));
    }
  }

  /**
   * Prune media the poller has *confirmed* deleted (an explicit 404).
   *
   * The predecessor pruned on any failed request, so a single rate-limited
   * cycle emptied the batch and removed the card outright. Only reach here
   * from a confirmed deletion.
   */
  @action
  private applyMediaDeleted(libraryId: string, deletedIds: string[]) {
    if (deletedIds.length === 0) return;
    const deleted = new Set(deletedIds);

    for (const notif of [...this.notifications]) {
      if (notif.type !== 'add-media-via-url' && notif.type !== 'upload-file') continue;
      if (notif.libraryId !== libraryId) continue;

      const survivingIds: string[] = [];
      const survivingTitles: string[] = [];
      notif.mediaIds.forEach((mediaId, i) => {
        if (deleted.has(mediaId)) return;
        survivingIds.push(mediaId);
        survivingTitles.push(notif.mediaTitles[i]);
      });
      if (survivingIds.length === notif.mediaIds.length) continue;

      if (survivingIds.length === 0) {
        // Every media in the batch is confirmed gone — the card has nothing
        // left to describe.
        this.remove(notif.id);
        continue;
      }

      notif.mediaIds = survivingIds;
      notif.mediaTitles = survivingTitles;
      const aliveSet = new Set(survivingIds);
      notif.erroredMediaIds = notif.erroredMediaIds.filter((id) => aliveSet.has(id));
      this.persistToStorage();
    }
  }
}

export const notificationStore = new NotificationStore();
